/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { motion, AnimatePresence } from 'motion/react';
import { X, MapPin, User, Phone, Share2, Calendar, ShieldCheck, ChevronLeft, ChevronRight, Check, Copy, MessageCircle, Truck } from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from './Toast';
import { useIsMobile } from '../utils/useIsMobile';
import { cloudinaryThumb } from '../utils/imageUrl';
import { toInternationalNumber } from '../utils/phone';
import { WhatsAppIcon, ViberIcon } from './BrandIcons';
import Modal from './Modal';

export default function ProductDetailsModal({ product, onClose }) {
  const toast = useToast();
  const isMobile = useIsMobile();
  const [justCopied, setJustCopied] = useState(false);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);

  // Zadrži poslednji proizvod da bi exit animacija imala šta da prikaže.
  const lastProductRef = useRef(product);
  if (product) lastProductRef.current = product;
  const shown = product || lastProductRef.current;

  const images = shown?.imageUrls?.length ? shown.imageUrls : (shown?.image ? [shown.image] : []);
  const [imgIndex, setImgIndex] = useState(0);

  // Reset to main image (index 0 since normalizeProduct reorders main first) on product change.
  useEffect(() => {
    setImgIndex(0);
    setPhoneRevealed(false);
    setMessageOpen(false);
  }, [product?.id]);

  const openMessenger = useCallback((app) => {
    const intl = toInternationalNumber(shown?.sellerPhone);
    if (!intl) {
      toast.error('Broj telefona prodavca nije dostupan.');
      return;
    }
    if (app === 'whatsapp') {
      const text = `Zdravo! Zainteresovan/a sam za vaš oglas "${shown?.name}" na Eko Pijaci.`;
      window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    } else {
      window.location.href = `viber://chat?number=${encodeURIComponent('+' + intl)}`;
    }
    setMessageOpen(false);
  }, [shown?.sellerPhone, shown?.name, toast]);

  const handleCallOrReveal = useCallback(async () => {
    const phone = shown?.sellerPhone;
    if (!phone) {
      toast.error('Broj telefona prodavca nije dostupan.');
      return;
    }
    if (isMobile) {
      window.location.href = `tel:${phone}`;
      return;
    }
    setPhoneRevealed(true);
    try {
      await navigator.clipboard.writeText(phone);
      toast.success('Broj kopiran u clipboard.');
    } catch {
      // Clipboard may fail (HTTP context, permissions); reveal still worked.
    }
  }, [isMobile, shown?.sellerPhone, toast]);

  const showPrev = useCallback(() => {
    if (images.length === 0) return;
    setImgIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const showNext = useCallback(() => {
    if (images.length === 0) return;
    setImgIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  // Strelice za navigaciju kroz galeriju (Escape i scroll lock rešava Modal).
  useEffect(() => {
    if (!product) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [product, showPrev, showNext]);

  const handleShare = async () => {
    if (!shown) return;
    const url = `${window.location.origin}/ponuda?oglas=${shown.slug || shown.id}`;
    const shareData = { title: shown.name, url };

    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 1800);
      toast.success('Link oglasa je kopiran u clipboard.');
    } catch {
      toast.error('Nije moguće kopirati link.');
    }
  };

  if (!shown) return null;

  return (
    <>
    <Modal
      isOpen={!!product}
      onClose={onClose}
      label={shown.name}
      zClassName="z-[110]"
      panelClassName="w-full max-w-4xl bg-white rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-3rem)] lg:max-h-[90vh]"
    >
            <button
              onClick={onClose}
              aria-label="Zatvori"
              className="absolute top-4 right-4 z-20 p-2 bg-white/90 backdrop-blur-md text-sage-600 hover:text-sage-900 hover:bg-white rounded-full shadow-md transition-all hover:scale-110 active:scale-95"
            >
              <X size={20} />
            </button>

            <div className="flex flex-col lg:grid lg:grid-cols-2 flex-1 min-h-0">
              {/* Image Section */}
              <div className="relative h-64 lg:h-full bg-sage-50 overflow-hidden flex-shrink-0">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.img
                    key={images[imgIndex] || 'empty'}
                    src={cloudinaryThumb(images[imgIndex], 1200)}
                    alt={shown.name}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>

                <div className="absolute top-6 left-6 flex gap-2 z-10">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-sage-800 text-xs font-bold rounded-full shadow-sm">
                    {shown.category}
                  </span>
                  {shown.status === 'Rasprodat' && (
                    <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider shadow-sm bg-red-500 text-white">
                      {shown.status}
                    </span>
                  )}
                </div>

                {images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={showPrev}
                      aria-label="Prethodna slika"
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/80 hover:bg-white backdrop-blur-md text-sage-700 rounded-full shadow-lg transition-all hover:scale-110 active:scale-95"
                    >
                      <ChevronLeft size={22} />
                    </button>
                    <button
                      type="button"
                      onClick={showNext}
                      aria-label="Sledeća slika"
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/80 hover:bg-white backdrop-blur-md text-sage-700 rounded-full shadow-lg transition-all hover:scale-110 active:scale-95"
                    >
                      <ChevronRight size={22} />
                    </button>

                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 bg-white/70 backdrop-blur-md px-3 py-1.5 rounded-full shadow">
                      {images.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setImgIndex(i)}
                          aria-label={`Idi na sliku ${i + 1}`}
                          className={`h-1.5 rounded-full transition-all ${
                            i === imgIndex ? 'w-5 bg-sage-700' : 'w-1.5 bg-sage-300 hover:bg-sage-500'
                          }`}
                        />
                      ))}
                    </div>

                    <span className="absolute top-6 right-20 z-10 px-2.5 py-1 bg-earth-900/60 text-white text-[11px] font-bold rounded-full backdrop-blur-sm">
                      {imgIndex + 1} / {images.length}
                    </span>
                  </>
                )}
              </div>

              {/* Content Section */}
              <div className="p-8 lg:p-12 overflow-y-auto overscroll-contain custom-scrollbar flex flex-col flex-1 min-h-0">
                <div className="flex-1">
                  <div className="flex justify-between items-start gap-4 mb-4 pr-12 lg:pr-14">
                    <h2 className="text-3xl lg:text-4xl font-serif font-bold text-earth-900 leading-tight">
                      {shown.name}
                    </h2>
                    <button
                      type="button"
                      onClick={handleShare}
                      aria-label="Podeli oglas"
                      title={justCopied ? 'Kopirano!' : 'Podeli oglas'}
                      className={`flex-shrink-0 mt-1 p-2.5 rounded-xl border transition-all hover:scale-105 active:scale-95 ${
                        justCopied
                          ? 'bg-sage-600 text-white border-sage-600 shadow-md shadow-sage-600/20'
                          : 'bg-sage-50 text-sage-700 border-sage-100 hover:bg-sage-100 hover:border-sage-200'
                      }`}
                    >
                      {justCopied ? <Check size={18} /> : <Share2 size={18} />}
                    </button>
                  </div>

                  <div className="flex items-center gap-3 mb-8">
                    <span className="text-3xl font-bold text-sage-700">
                      {shown.price} RSD
                    </span>
                    <span className="text-lg text-sage-600/60 font-medium">/ {shown.unit}</span>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-bold text-earth-900 uppercase tracking-wider mb-2">Opis proizvoda</h4>
                      <p className="text-sage-800/80 leading-relaxed">
                        {shown.description}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-sage-50 rounded-2xl flex items-center gap-3 border border-sage-100">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-sage-600 shadow-sm">
                          <MapPin size={20} />
                        </div>
                        <div>
                          <p className="text-[11px] uppercase font-bold text-sage-500">Lokacija</p>
                          <p className="text-sm font-bold text-earth-900">{shown.location}</p>
                        </div>
                      </div>
                      <div className="p-4 bg-sage-50 rounded-2xl flex items-center gap-3 border border-sage-100">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-sage-600 shadow-sm">
                          <Calendar size={20} />
                        </div>
                        <div>
                          <p className="text-[11px] uppercase font-bold text-sage-500">Objavljeno</p>
                          <p className="text-sm font-bold text-earth-900">{shown.createdAt}</p>
                        </div>
                      </div>
                      {shown.deliveryMethodLabel && (
                        <div className="col-span-2 p-4 bg-sage-50 rounded-2xl flex items-center gap-3 border border-sage-100">
                          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-sage-600 shadow-sm">
                            <Truck size={20} />
                          </div>
                          <div>
                            <p className="text-[11px] uppercase font-bold text-sage-500">Način preuzimanja</p>
                            <p className="text-sm font-bold text-earth-900">{shown.deliveryMethodLabel}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-sage-100 pt-6">
                      <h4 className="text-sm font-bold text-earth-900 uppercase tracking-wider mb-3">Podaci o prodavcu</h4>
                      <div className="flex items-center gap-4 p-4 bg-white border border-sage-100 rounded-2xl shadow-sm">
                        <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center text-sage-600">
                          <User size={24} />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-earth-900">{shown.householdName || shown.sellerName}</p>
                          {shown.ownerName && (
                            <p className="text-xs text-sage-700 font-medium">{shown.ownerName}</p>
                          )}
                          <div className="flex items-center gap-1 text-xs text-sage-500 mt-0.5">
                            <ShieldCheck size={14} className="text-green-500" />
                            <span>Verifikovano domaćinstvo</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-12 flex flex-col gap-3">
                  <motion.button
                    onClick={handleCallOrReveal}
                    whileHover={shown.status === 'Aktivan' ? { scale: 1.02 } : {}}
                    whileTap={shown.status === 'Aktivan' ? { scale: 0.98 } : {}}
                    disabled={shown.status !== 'Aktivan'}
                    className={`flex-1 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg transition-all ${
                      shown.status === 'Aktivan'
                      ? 'bg-sage-600 text-white shadow-sage-600/20 hover:bg-sage-700'
                      : 'bg-sage-200 text-sage-400 shadow-none cursor-not-allowed'
                    }`}
                  >
                    {shown.status !== 'Aktivan' ? (
                      <>
                        <Phone size={20} />
                        Proizvod rasprodat
                      </>
                    ) : isMobile ? (
                      <>
                        <Phone size={20} />
                        Pozovi prodavca
                      </>
                    ) : phoneRevealed ? (
                      <>
                        <Copy size={18} />
                        {shown.sellerPhone}
                      </>
                    ) : (
                      <>
                        <Phone size={20} />
                        Prikaži broj
                      </>
                    )}
                  </motion.button>

                  {isMobile && shown.status === 'Aktivan' && (
                    <motion.button
                      onClick={() => setMessageOpen(true)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg transition-all bg-white text-sage-700 border-2 border-sage-200 hover:bg-sage-50"
                    >
                      <MessageCircle size={20} />
                      Pošalji poruku
                    </motion.button>
                  )}
                </div>
              </div>
            </div>
    </Modal>

    <Modal
      isOpen={messageOpen}
      onClose={() => setMessageOpen(false)}
      label="Pošalji poruku prodavcu"
      zClassName="z-[120]"
      panelClassName="w-full max-w-sm bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
    >
      <div className="p-8">
        <button
          onClick={() => setMessageOpen(false)}
          aria-label="Zatvori"
          className="absolute top-4 right-4 p-2 text-sage-600 hover:text-sage-900 hover:bg-sage-50 rounded-full transition-all"
        >
          <X size={20} />
        </button>

        <h3 className="text-2xl font-serif font-bold text-earth-900 mb-1">Pošalji poruku</h3>
        <p className="text-sm text-sage-700/80 mb-6">Izaberi aplikaciju za kontakt sa prodavcem.</p>

        <div className="space-y-3">
          <button
            onClick={() => openMessenger('whatsapp')}
            className="w-full py-4 px-5 rounded-2xl font-bold flex items-center gap-3 transition-all bg-[#25D366] text-white shadow-lg shadow-[#25D366]/20 hover:brightness-105 active:scale-[0.98]"
          >
            <WhatsAppIcon size={22} />
            WhatsApp
          </button>
          <button
            onClick={() => openMessenger('viber')}
            className="w-full py-4 px-5 rounded-2xl font-bold flex items-center gap-3 transition-all bg-[#7360F2] text-white shadow-lg shadow-[#7360F2]/20 hover:brightness-105 active:scale-[0.98]"
          >
            <ViberIcon size={22} />
            Viber
          </button>
        </div>
      </div>
    </Modal>
    </>
  );
}
